import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Download, Search, Calendar, Mail, User } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

interface DiagnosisResult {
  id: string;
  email: string;
  diagnosis_type: string;
  result_type: string;
  scores: any;
  total_score: number;
  type_info: any;
  created_at: string;
}

const AdminDashboard = () => {
  const [results, setResults] = useState<DiagnosisResult[]>([]);
  const [filteredResults, setFilteredResults] = useState<DiagnosisResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    fetchResults();
  }, []);

  useEffect(() => {
    filterResults();
  }, [results, searchTerm, filterType]);

  const fetchResults = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('diagnosis_results_2025_11_12_13_00')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('データ取得エラー:', error);
        toast({
          title: "エラー",
          description: "診断結果の取得に失敗しました。",
          variant: "destructive",
        });
        return;
      }

      setResults(data || []);
      console.log('取得した診断結果:', data);
    } catch (error) {
      console.error('データ取得エラー:', error);
      toast({
        title: "エラー",
        description: "診断結果の取得に失敗しました。",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const filterResults = () => {
    let filtered = results;

    // 診断タイプでフィルタ
    if (filterType !== 'all') {
      filtered = filtered.filter(result => result.diagnosis_type === filterType);
    }

    // 検索語でフィルタ
    if (searchTerm) {
      filtered = filtered.filter(result => 
        result.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        result.result_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        result.type_info?.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredResults(filtered);
  };

  const exportToCSV = () => {
    if (filteredResults.length === 0) {
      toast({
        title: "エクスポートできません",
        description: "エクスポートするデータがありません。",
        variant: "destructive",
      });
      return;
    }

    const headers = [
      'メールアドレス',
      '診断タイプ',
      '結果タイプ',
      '結果名',
      '合計スコア',
      '診断日時'
    ];

    const csvData = filteredResults.map(result => [
      result.email,
      result.diagnosis_type === 'detailed' ? '詳細診断' : '簡単チェック',
      result.result_type,
      result.type_info?.name || '',
      result.total_score || '',
      new Date(result.created_at).toLocaleString('ja-JP')
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `診断結果_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: "エクスポート完了",
      description: `${filteredResults.length}件の診断結果をCSVファイルでダウンロードしました。`,
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDiagnosisTypeBadge = (type: string) => {
    return type === 'detailed' ? (
      <Badge variant="default" className="bg-blue-100 text-blue-800">詳細診断</Badge>
    ) : (
      <Badge variant="secondary" className="bg-green-100 text-green-800">簡単チェック</Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-nordic flex items-center justify-center">
        <Card className="w-96">
          <CardContent className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p>診断結果を読み込み中...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-nordic p-4">
      <div className="max-w-7xl mx-auto">
        {/* ヘッダー */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  メニューに戻る
                </Button>
              </Link>
              <h1 className="text-3xl font-bold text-zen-dark">管理者ダッシュボード</h1>
            </div>
            <Button onClick={exportToCSV} className="bg-gradient-zen hover:opacity-90">
              <Download className="w-4 h-4 mr-2" />
              CSVエクスポート
            </Button>
          </div>

          {/* 統計情報 */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <User className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="text-sm text-gray-600">総診断数</p>
                    <p className="text-2xl font-bold">{results.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="text-sm text-gray-600">詳細診断</p>
                    <p className="text-2xl font-bold">
                      {results.filter(r => r.diagnosis_type === 'detailed').length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-purple-600" />
                  <div>
                    <p className="text-sm text-gray-600">簡単チェック</p>
                    <p className="text-2xl font-bold">
                      {results.filter(r => r.diagnosis_type === 'happiness_check').length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <Search className="w-5 h-5 text-orange-600" />
                  <div>
                    <p className="text-sm text-gray-600">表示中</p>
                    <p className="text-2xl font-bold">{filteredResults.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* フィルター */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1">
              <Input
                placeholder="メールアドレスまたは結果タイプで検索..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant={filterType === 'all' ? 'default' : 'outline'}
                onClick={() => setFilterType('all')}
                size="sm"
              >
                全て
              </Button>
              <Button
                variant={filterType === 'detailed' ? 'default' : 'outline'}
                onClick={() => setFilterType('detailed')}
                size="sm"
              >
                詳細診断
              </Button>
              <Button
                variant={filterType === 'happiness_check' ? 'default' : 'outline'}
                onClick={() => setFilterType('happiness_check')}
                size="sm"
              >
                簡単チェック
              </Button>
            </div>
          </div>
        </div>

        {/* 診断結果一覧 */}
        {filteredResults.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <p className="text-gray-500">診断結果がありません。</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {filteredResults.map((result) => (
              <Card key={result.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <Mail className="w-4 h-4 text-gray-500" />
                        <span className="font-medium">{result.email}</span>
                        {getDiagnosisTypeBadge(result.diagnosis_type)}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-600">診断結果</p>
                          <p className="font-semibold text-lg">{result.type_info?.name}</p>
                          {result.total_score && (
                            <p className="text-sm text-gray-600">合計スコア: {result.total_score}点</p>
                          )}
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">診断日時</p>
                          <p className="font-medium">{formatDate(result.created_at)}</p>
                        </div>
                      </div>
                      {result.type_info?.description && (
                        <div className="mt-3">
                          <p className="text-sm text-gray-600">説明</p>
                          <p className="text-sm">{result.type_info.description}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      
      {/* 外部サイトへの戻るボタン */}
      <div className="mt-8 pt-6 border-t border-muted text-center">
        <a 
          href="https://hokuou-wellbeing.jp/" 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-block"
        >
          <Button 
            variant="outline" 
            className="bg-gradient-zen hover:opacity-90 transition-zen text-white border-none"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            北欧ウェルビーイングに戻る
          </Button>
        </a>
      </div>
    </div>
  );
};

export default AdminDashboard;